// components/ui/Button.jsx
// Created: 2026-07-20
// Project: Giving Words to Voices
// Version: 1.0.0
//
// The Button primitive — the first of the six. The reviews measured 517 distinct
// button style signatures across 564 buttons because every one was authored
// inline. This is the one place a button's shape is decided; call sites pick a
// variant and a size, nothing else.
//
// It reads the theme and the token contract, so a button is correct on every
// scheme without the call site knowing which scheme is active. Text colour on
// filled variants is computed with contrastText — the fix for the illegible
// MARK ERROR button, now structural rather than per-site.

import React from 'react';
import { Loader } from 'lucide-react';
import { contrastText } from '../../constants/theme';
import { SPACE, RADIUS, TYPE, WEIGHT, isDarkTheme } from '../../constants/tokens';
import { DANGER } from '../../constants/palette';
import { Icon } from '../Icon';

// Padding + font per size.
const SIZES = {
  sm: { padding: `${SPACE[1]} ${SPACE[2]}`, font: TYPE.xs, gap: SPACE[1], icon: 12, minH: '28px' },
  md: { padding: `${SPACE[2]} ${SPACE[3]}`, font: TYPE.sm, gap: SPACE[2], icon: 14, minH: '36px' },
  lg: { padding: `${SPACE[3]} ${SPACE[4]}`, font: TYPE.base, gap: SPACE[2], icon: 16, minH: '44px' },
};

// Each variant returns its colours from the theme. Filled variants derive their
// text colour so it always contrasts — no hardcoded white.
function variantStyle(variant, theme) {
  const isDark = isDarkTheme(theme);
  switch (variant) {
    case 'primary':
      return { background: theme.highlight, color: theme.highlightText || contrastText(theme.highlight), border: '1px solid transparent' };
    case 'accent': // the "mark error" red / brand accent
      return { background: theme.accent, color: contrastText(theme.accent), border: '1px solid transparent' };
    case 'danger':
      return { background: theme.danger || theme.accent, color: contrastText(theme.danger || theme.accent), border: '1px solid transparent' };
    case 'success':
      return { background: theme.success, color: contrastText(theme.success), border: '1px solid transparent' };
    case 'secondary': // surface fill
      return { background: theme.card, color: theme.text, border: `1px solid ${theme.border}` };
    case 'outline':
      return { background: 'transparent', color: theme.text, border: `1px solid ${theme.border}` };
    case 'ghost':
      return { background: 'transparent', color: theme.textSub, border: '1px solid transparent' };
    case 'danger-ghost': // the delete convention: red text, no fill
      return { background: 'transparent', color: theme.danger || DANGER, border: `1px solid ${(theme.danger || DANGER)}${isDark ? '55' : '44'}` };
    default:
      return { background: theme.card, color: theme.text, border: `1px solid ${theme.border}` };
  }
}

export default function Button({
  children,
  variant = 'secondary',
  size = 'md',
  icon = null,          // a lucide icon component
  iconRight = null,
  loading = false,
  disabled = false,
  active = false,       // segmented / toggle "on" state
  fullWidth = false,
  title,
  ariaLabel,
  style = {},
  ...rest
}) {
  // theme must be passed (the app has no ThemeContext yet). Read from rest so
  // call sites keep passing it as they do everywhere else.
  const theme = rest.theme || {};
  delete rest.theme;

  const sz = SIZES[size] || SIZES.md;
  const v = variantStyle(variant, theme);
  const isDisabled = disabled || loading;

  // Active state for toggles: fill with the highlight, contrast the text.
  const activeStyle = active
    ? { background: theme.highlight, color: theme.highlightText || contrastText(theme.highlight), border: `1px solid ${theme.highlight}` }
    : {};

  const IconCmp = loading ? Loader : icon;

  return (
    <button
      type="button"
      title={title}
      aria-label={ariaLabel}
      aria-pressed={active || undefined}
      disabled={isDisabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: sz.gap,
        padding: sz.padding,
        minHeight: sz.minH,
        fontSize: sz.font,
        fontWeight: WEIGHT.semibold,
        fontFamily: 'inherit',
        lineHeight: 1.1,
        borderRadius: RADIUS.md,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        // Disabled state is VISIBLE — the review found ~59 buttons disabled
        // with no visual change at all.
        opacity: isDisabled ? 0.5 : 1,
        width: fullWidth ? '100%' : undefined,
        whiteSpace: 'nowrap',
        transition: 'background 0.12s, opacity 0.12s, border-color 0.12s',
        ...v,
        ...activeStyle,
        ...style,
      }}
      {...rest}
    >
      {IconCmp && (
        <Icon
          icon={IconCmp}
          size={sz.icon}
          style={loading ? { animation: 'spin 1s linear infinite' } : undefined}
        />
      )}
      {children}
      {iconRight && !loading && <Icon icon={iconRight} size={sz.icon} />}
    </button>
  );
}
